import type { InsertObligationChangeAttestationOneMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

export interface InsertObligationChangeAttestationTRPCInput {
  ObligationChangeId: string;
}

/**
 * Maps the TRPC mutation response to match the GraphQL mutation structure so
 * consumers can branch on success/error without knowing which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertObligationChangeAttestationOneMutation => {
  return {
    insertChildObligationChangeAttestation: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertObligationChangeAttestationTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligationChangeAttestation.insert.mutationOptions({
      onSuccess: async () => {
        // Legacy Apollo path evicted the coarse `obligation_change` field,
        // so invalidate the whole obligation-change sub-router (the family
        // backing getObligationChangeById).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligationChange.pathKey(),
        });
      },
    }),
  });

  return {
    insertObligationChangeAttestation: async (
      variables: InsertObligationChangeAttestationTRPCInput
    ): Promise<InsertObligationChangeAttestationOneMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
